"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import type { PortfolioProject } from "../data";
import { Icons, Images } from "@assets/assets";

interface HalfWidthCardProps {
  project: PortfolioProject;
  theme?: "light" | "dark";
  onClick: () => void;
}

export default function HalfWidthCard({
  project,
  theme = "light",
  onClick,
}: HalfWidthCardProps) {
  const isDark = theme === "dark";

  const titleColor = isDark ? "text-white" : "text-black";
  const descColor = isDark ? "text-white/50" : "text-[#636363]";
  const tagText = isDark ? "text-white" : "text-[#00040BCC]";
  const tagBg = isDark ? "bg-white/10 backdrop-blur-md" : "bg-black/5";
  const dividerColor = isDark ? "lg:divide-white/20" : "lg:divide-white/50";
  const groupBg = isDark ? "lg:bg-white/5" : "lg:bg-[#ECF0F6]";
  const arrowBtnBg = isDark ? "bg-white" : "bg-white";
  const arrowImgClasses = isDark ? "invert" : "";

  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        const el = textRef.current;
        if (el) {
          setIsOverflowing(el.scrollHeight > el.offsetHeight);
        }
      });
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkOverflow, 150);
    };

    const resizeObserver = new ResizeObserver(debouncedCheck);
    const initialTimeout = setTimeout(checkOverflow, 300);

    if (textRef.current) resizeObserver.observe(textRef.current);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const shouldShowButton = isOverflowing || isExpanded;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className="success-card col-span-1 flex flex-col justify-start [&_*]:transition-all [&_*]:duration-600 [&_*]:ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer"
      onClick={onClick}
    >
      {/* Image Container with 3D hover */}
      <div className="image-container group relative overflow-hidden w-full aspect-[1/1] lg:aspect-[16/7] 2xl:aspect-[1/1] transition-transform duration-700 hover:scale-95 hover:-rotate-y-6 [transform-style:preserve-3d]">
        <Image
          src={
            project?.colspan === 1
              ? project?.newSliderBg || Images?.imagePlaceholder
              : Images?.imagePlaceholder
          }
          alt={project.title}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-cover transition-transform duration-700"
        />

        {/* Arrow Button */}
        <div
          className={`arrow-button absolute bottom-5 right-5 size-12 ${arrowBtnBg} rounded-full flex items-center justify-center transition-all duration-500 group-hover:rotate-[-45deg] group-hover:bg-[#225BD4]`}
        >
          <Image
            src={Icons?.arrowLeft}
            alt=""
            width={15}
            height={15}
            className={`arrow-button-img transition-all invert duration-500 ${arrowImgClasses} group-hover:invert-0`}
          />
        </div>
      </div>

      {/* Title */}
      <h3
        className={`mt-8 mb-3 text-[length:var(--sucess-card-title)] font-medium clash-font capitalize leading-[110%] ${titleColor}`}
      >
        {project.title}
      </h3>

      {/* Description with read-more */}
      <div className="flex flex-col items-start w-full min-h-[125px] md:min-h-[180px]">
        <p
          ref={textRef}
          className={`text-base lg:text-xl font-medium leading-[180%] ${descColor} transition-all duration-300 ${!isExpanded ? "line-clamp-3 overflow-hidden" : ""
            }`}
        >
          {project.description}
        </p>

        {shouldShowButton && (
          <button
            onClick={handleToggle}
            className="mt-2 inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-all"
            aria-label={isExpanded ? "Read less" : "Read more"}
          >
            {isExpanded ? (
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m18 15-6-6-6 6" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Tags / Categories */}
      <div
        className={`tags-container mt-5 flex flex-wrap gap-3 
    lg:inline-flex lg:flex-nowrap lg:gap-0 lg:w-fit 
    ${dividerColor} lg:divide-x-2 ${groupBg} 
    lg:rounded-[50px] lg:overflow-hidden`}
      >
        {project.categories?.map((cat) => (
          <div
            key={cat.id}
            className={`tag-item ${tagBg} flex items-center justify-center px-5 h-9 xl:h-[49px] 
        rounded-2xl lg:rounded-none lg:bg-transparent lg:px-8 
        ${tagText} font-medium text-center whitespace-nowrap`}
          >
            {cat.name}
          </div>
        ))}
      </div>
    </div>
  );
}